'use client';

/**
 * AstorStepper Component
 *
 * Custom horizontal stepper with mint fills, ring halo on the current step,
 * mint connectors for completed segments, and an optional "In progress"
 * sub-label rendered under the current step.
 *
 * Reusable for any multi-step lifecycle (loan stages, onboarding wizards,
 * application status pipelines). Name is generic, NOT borrower-specific.
 *
 * Size:
 *   default → 26px circles + 14px label + 18px halo offset
 *   compact → 22px circles + 12px label + 16px halo offset
 */

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export type AstorStepperSize = 'default' | 'compact';

export interface AstorStepperProps {
  /** Stage labels rendered under each circle. */
  stages: string[];
  /** 0-indexed current stage. Stages with index < current render as done. */
  current: number;
  /** Size preset. */
  size?: AstorStepperSize;
  /** Sub-label rendered under the current step (e.g. "In progress"). */
  currentSubLabel?: ReactNode;
  /** Sx on outer Box. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Size resolution
// ============================================================================

interface SizeTokens {
  circle: number;
  haloOffset: number;
  iconSize: number;
  labelSize: string;
  subLabelSize: string;
  connectorHeight: number;
  gap: number;
}

const SIZE_TOKENS: Record<AstorStepperSize, SizeTokens> = {
  default: {
    circle: 26,
    haloOffset: 4,
    iconSize: 14,
    labelSize: '0.8125rem', // 13px
    subLabelSize: '0.6875rem', // 11px
    connectorHeight: 2,
    gap: 12,
  },
  compact: {
    circle: 22,
    haloOffset: 4,
    iconSize: 12,
    labelSize: '0.75rem', // 12px
    subLabelSize: '0.625rem', // 10px
    connectorHeight: 2,
    gap: 8,
  },
};

// ============================================================================
// Step circle
// ============================================================================

type StepKind = 'done' | 'current' | 'future';

interface StepCircleProps {
  kind: StepKind;
  index: number;
  tokens: SizeTokens;
}

function StepCircle({ kind, index, tokens }: StepCircleProps) {
  const theme = useTheme();
  const mint = theme.palette.primary.main;
  const contrast = theme.palette.primary.contrastText;
  const isDone = kind === 'done';
  const isCurrent = kind === 'current';
  const isFuture = kind === 'future';

  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        width: tokens.circle,
        height: tokens.circle,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontWeight: 600,
        fontSize: '0.75rem',
        fontFeatureSettings: '"tnum"',
        lineHeight: 1,
        backgroundColor: isFuture ? theme.palette.background.paper : mint,
        color: isFuture ? theme.palette.text.secondary : contrast,
        border: `1px solid ${isFuture ? theme.palette.divider : mint}`,
        boxShadow: isCurrent ? `0 0 0 ${tokens.haloOffset}px ${alpha(mint, 0.18)}` : 'none',
        transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: 150 }),
      }}
    >
      {isDone ? <CheckIcon sx={{ fontSize: tokens.iconSize }} /> : index + 1}
    </Box>
  );
}

// ============================================================================
// Connector
// ============================================================================

interface ConnectorProps {
  done: boolean;
  tokens: SizeTokens;
}

function Connector({ done, tokens }: ConnectorProps) {
  const theme = useTheme();
  const mint = theme.palette.primary.main;
  return (
    <Box
      aria-hidden
      sx={{
        flex: 1,
        minWidth: 12,
        height: tokens.connectorHeight,
        borderRadius: 1,
        backgroundColor: done ? mint : theme.palette.divider,
        mx: 1,
        // Vertically align with the circle centre — pull up by half the
        // (circle - connector) delta so connectors hug the middle of circles.
        position: 'relative',
        top: -((tokens.circle - tokens.connectorHeight) / 2),
      }}
    />
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AstorStepper({ stages, current, size = 'default', currentSubLabel, sx }: AstorStepperProps) {
  const tokens = SIZE_TOKENS[size];
  const total = stages.length;

  return (
    <Box
      role="list"
      aria-label="progress"
      sx={composeSx(
        {
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
        },
        sx,
      )}
    >
      {stages.map((stage, idx) => {
        let kind: StepKind = 'future';
        if (idx < current) kind = 'done';
        else if (idx === current) kind = 'current';

        const isLast = idx === total - 1;
        const segmentComplete = idx < current; // connector after a done step is mint

        return (
          <Box key={`${idx}-${stage}`} sx={{ display: 'flex', flex: isLast ? '0 0 auto' : 1, minWidth: 0 }}>
            <Box
              role="listitem"
              aria-current={kind === 'current' ? 'step' : undefined}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                minWidth: tokens.circle,
              }}
            >
              <StepCircle kind={kind} index={idx} tokens={tokens} />
              <Typography
                sx={{
                  fontSize: tokens.labelSize,
                  fontWeight: kind === 'current' ? 600 : 500,
                  color: kind === 'future' ? 'text.secondary' : 'text.primary',
                  textAlign: 'center',
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 140,
                }}
              >
                {stage}
              </Typography>
              {kind === 'current' && currentSubLabel && (
                <Typography
                  sx={{
                    fontSize: tokens.subLabelSize,
                    color: 'primary.main',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  {currentSubLabel}
                </Typography>
              )}
            </Box>
            {!isLast && <Connector done={segmentComplete} tokens={tokens} />}
          </Box>
        );
      })}
    </Box>
  );
}

AstorStepper.displayName = 'AstorStepper';

export default AstorStepper;
